import { RWSService } from "@rws-framework/client";

import type {
  DashboardResponse,
  Course as CourseRaw,
  Professor,
  ProgramRead,
  ProfessorsInfo,
  CourseClass,
  Tag,
} from "./typesRaw";
import type {
  CalendarGrouping,
  CatalogCourse,
  Course,
  ProductClass,
  Section,
  SectionInstructor,
  Term,
} from "./types";
import { keyBy, stripHTMLTags } from "src/helpers/utils";
import { CourseUserService } from "./CourseServiceUser";

/** Transforms backend data types to frontend structures. Used by CourseService. */
class CourseFormatService extends RWSService {
  /**
   * The following table describes relations between `programs`, `sections`, `section_templates` and fields like `parent`, `use_parent_template`, `use_section_template`
   *
    | section_type     | parent | use_parent_template | use_section_template |     | what it is                  | parent is ...                         | uses course_data of ... |
    | ---------------- | ------ | ------------------- | -------------------- | --- | --------------------------- | ------------------------------------- | ----------------------- |
    | -                |        |                     |                      |     | standalone course           | -                                     | owned                   |
    | program_master   | X      |                     |                      |     | program                     | catalog course/other program (cloned) | owned                   |
    | section_template | X      | -                   |                      |     | section template (unhooked) | program                               | owned                   |
    | section_template | X      | X                   |                      |     | section template            | program                               | X                       |
    | section          | X      | -                   | -                    |     | section (unhooked)          | program                               | owned                   |
    | section          | X      | X                   | -                    |     | section                     | program                               | owned                   |
    | section          | X      | X                   | Y                    |     | section                     | program                               | Y                       |
    | section          | X      | -                   | Y                    |     | section                     | program                               | Y                       |
   */
  static formatCourses(
    rawCourses: CourseRaw[],
    professors: Professor[],
    terms: Tag,
  ): Course[] {
    const rawStandAloneCourses = rawCourses.filter((c) => !c.section_type);
    const standAloneCourses = rawStandAloneCourses.map((c) => {
      const course = CourseFormatService.defaultFormatCourse(
        c,
        professors,
        terms,
      );
      const section: Section = CourseFormatService.formatSection(
        c,
        professors,
        null,
      );

      return { ...course, section };
    });

    const programs = rawCourses.filter(
      (c) => c.section_type === "program_master",
    );
    const courses = programs
      .map((p): Course => {
        const course = CourseFormatService.defaultFormatCourse(
          p,
          professors,
          terms,
        );
        const children = p.children || [];
        const sectionTemplates = children.filter(
          (c) => c.section_type === "section_template",
        );
        const sections = children.filter((c) => c.section_type === "section");

        return {
          ...course,
          calendarGroupings: CourseFormatService.formatCalendarGroupings(
            sections,
            sectionTemplates,
            professors,
            p,
          ),
        };
      })
      .filter(Boolean);

    return [...courses, ...standAloneCourses];
  }

  static formatCalendarGroupings(
    sections: CourseRaw[],
    sectionTemplates: CourseRaw[],
    personnel: Professor[],
    masterCourse: CourseRaw,
  ): CalendarGrouping[] {
    const unhookedSections = sections.filter((s) => !s.use_section_template);

    const sectionTemplateGroups: CalendarGrouping[] = sectionTemplates
      .map((st) => ({
        id: st._id,
        name: st.title,
        sections: sections
          .filter((s) => s.use_section_template === st._id)
          .map((s) =>
            CourseFormatService.formatSection(s, personnel, masterCourse),
          )
          .sort((a, b) =>
            stripHTMLTags(a.name).localeCompare(stripHTMLTags(b.name)),
          ),
      }))
      .map(CourseFormatService.validateSectionRoles);

    const unhookedGroups: CalendarGrouping[] = unhookedSections.map((s) => ({
      id: s._id,
      name: s.title,
      sections: [CourseFormatService.formatSection(s, personnel, masterCourse)],
    }));

    return [...sectionTemplateGroups, ...unhookedGroups].sort((a, b) =>
      stripHTMLTags(a.name).localeCompare(stripHTMLTags(b.name)),
    );
  }

  /** used both for standalone courses and programs */
  static defaultFormatCourse(
    course: CourseRaw,
    personnel: Professor[],
    terms: Tag,
  ): Course {
    const professorsById = keyBy(personnel, (p) => p._id);
    const isProgramMaster = course.section_type === "program_master";

    const instructors = (course.instructors || [])
      .filter((id) => professorsById[id])
      .map((id) => ({ id, courseRole: "instructor" as const }));

    const assistants = (course.assistants || [])
      .filter((id) => professorsById[id])
      .map((id) => ({ id, courseRole: "assistant" as const }));

    return {
      id: course._id,
      slug: course.slug,
      title: course.title,
      reseller: course.reseller,
      institution:
        course.professors_info.find((i) => i.institution)?.institution || "",
      calendarGroupings: null,
      section: null,
      image: course.collegeLogoUrl,
      professors: course.professors,
      openDate: course.open_date,
      term: CourseFormatService.detectTerm(course, terms),
      archived: course.status === "archived",
      // by default go to course (true) unless explicitly set to `false`
      courseEntry: course.course_entry === undefined || course.course_entry,
      instructors: [...instructors, ...assistants].map(
        ({ id, courseRole }) => ({
          instructor: {
            id,
            name: `${professorsById[id].name} ${professorsById[id].surname}`,
            email: professorsById[id].email,
            firstname: professorsById[id].name,
            surname: professorsById[id].surname,
          },
          role:
            courseRole === "instructor"
              ? "instructor_edit_course"
              : course.assistants_no_mgr_rights
                ? "instructor_no_edit"
                : "instructor_edit_calendar",
        }),
      ),
    };
  }

  static formatSection(
    course: CourseRaw,
    personnel: Professor[],
    masterCourse: CourseRaw | null,
  ): Section {
    const professorsById = keyBy(personnel, (p) => p._id);

    return {
      id: course._id,
      slug: course.slug,
      name: course.title,
      studentCount: course.student_count || 0,
      instructors: []
        .concat(
          course.instructors
            .filter((id) => professorsById[id])
            .map(
              (id): SectionInstructor => ({
                instructor: {
                  id,
                  name: `${professorsById[id].name} ${professorsById[id].surname}`,
                  email: professorsById[id].email,
                  firstname: professorsById[id].name,
                  surname: professorsById[id].surname,
                },
                role: "instructor_edit_calendar",
              }),
            ),
        )
        .concat(
          course.assistants
            .filter((id) => professorsById[id])
            .map(
              (id): SectionInstructor => ({
                instructor: {
                  id,
                  name: `${professorsById[id].name} ${professorsById[id].surname}`,
                  email: professorsById[id].email,
                  firstname: professorsById[id].name,
                  surname: professorsById[id].surname,
                },
                role: course.assistants_no_mgr_rights
                  ? "instructor_no_edit"
                  : "instructor_edit_calendar",
              }),
            ),
        ),
      courseEntry:
        course.course_entry !== undefined
          ? course.course_entry
          : // inherit course_entry if not defined
            masterCourse
            ? // by default go to course (true) unless explicitly set to `false`
              masterCourse.course_entry === undefined ||
              masterCourse.course_entry
            : true,
    };
  }

  /**
   * Database structure does not prevent cases when the same instructor has calendar edit rights in one section,
   * but does not have them in another section within the same calendar.
   * ```
   * # db.courses
   *   course:
   *     calendar1:
   *       section1.1:
   *         # ...
   *         instructors: [XYZ]
   *
   *      section1.2:
   *        # ...
   *        assistants: [XYZ]
   *        assistants_no_mgr_rights: true
   *    calendar2:
   *      # ...
   * ```
   * This function ensures role consistency by picking the highest permission defined across all sections within the calendar.
   * (If instructor has calendar edit rights in at least one, we make sure they have them on all sections in that calendar)
   */
  static validateSectionRoles(calendar: CalendarGrouping): CalendarGrouping {
    const roleLookup = keyBy(
      CourseUserService.findInstructorsInCalendar(calendar),
      (i) => i.instructor.id,
    );

    return {
      ...calendar,
      sections: calendar.sections.map((s) => ({
        ...s,
        instructors: s.instructors.map((i) => ({
          ...i,
          role:
            roleLookup[i.instructor.id]?.role ??
            CourseUserService.DEFAULT_SECTION_ROLE,
        })),
      })),
    };
  }

  static formatCatalogCourses(
    courses: CourseRaw[],
    classes: CourseClass[],
  ): CatalogCourse[] {
    const classesByName = keyBy(classes, (c) => c.name);
    const now = new Date().toISOString();

    return courses.map((c) => {
      const productClass: ProductClass =
        // TODO: is it possible to match by id instead of by name?
        c.class && classesByName[c.class]
          ? {
              name: classesByName[c.class].name,
              value: classesByName[c.class]._id,
            }
          : null;

      return {
        id: c._id,
        slug: c.slug,
        title: c.title,
        reseller: c.reseller,
        institution:
          c.professors_info.find((i) => i.institution)?.institution || "",
        calendarGroupings: null,
        section: null,
        image: c.collegeLogoUrl,
        professors: c.professors,
        archived: c.status === "archived",
        catalog: { productClass, status: c.status || "active" },
      };
    });
  }

  static detectTerm(course: CourseRaw, terms: Tag): Term | null {
    if (!course.tags) return null;

    const termValue = terms.values.find((v) => course.tags.includes(v._id));

    if (!termValue) return null;

    return {
      date: termValue.sorting_key,
      name: termValue.value,
      id: termValue._id,
    };
  }

  static applyTermToTags(tags: CourseRaw['tags'], term: Term['id'], terms: Term[]) {
    const termIds = terms.map(t => t.id);

    return (
      tags
        // remove all other term tags
        .filter((t) => !termIds.includes(t))
        // add new one
        .concat(term)
    );
  }

  /** Backend requires zonal timestamps, but the ones returned by it are lacking the Z (even thought they are zonal) */
  static ensureProfessorsInfoDateFormat(info: ProfessorsInfo): ProfessorsInfo {
    return {
      ...info,
      fromDate:
        !info.fromDate || info.fromDate.endsWith("Z")
          ? info.fromDate
          : `${info.fromDate}Z`,
      toDate:
        !info.toDate || info.toDate.endsWith("Z")
          ? info.toDate
          : `${info.toDate}Z`,
    };
  }

  static setInstructorsAndAssistants(
    courseInstructors: SectionInstructor[],
  ): Pick<
    CourseRaw,
    "instructors" | "assistants" | "assistants_no_mgr_rights"
  > {
    const assistants = courseInstructors
      .filter((i) => i.role === "instructor_no_edit")
      .map((i) => i.instructor.id);
    const instructors = courseInstructors
      .filter((i) => i.role === "instructor_edit_calendar")
      .map((i) => i.instructor.id);
    const assistants_no_mgr_rights = courseInstructors.some(
      (i) => i.role === "instructor_no_edit",
    );

    return {
      assistants,
      instructors,
      assistants_no_mgr_rights,
    };
  }

  static formatTerms(termTag: Tag): Term[] {
    return termTag.values.map((v) => ({
      id: v._id,
      date: v.sorting_key,
      name: v.value,
    }));
  }
}

export default CourseFormatService.getSingleton();

export { CourseFormatService };
